import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, ChevronLeft, X, Headphones, Rewind, FastForward, Lock } from "lucide-react";
import { AudioAPI } from "../lib/api";
import { useAuth } from "../lib/auth";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const SPEEDS = [1, 1.25, 1.5, 2];

/**
 * Full-screen, branded audio player overlay.
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - article: { title, dek, author, publishedAt, image, audio }
 */
export const AudioPlayerModal = ({ open, onClose, article }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);
  const beaconFiredRef = useRef(false);
  const { user, requireAuth } = useAuth();

  // Per user directive: ALL audio playback requires a signed-in account.
  // Additionally, "premium" audio remains gated even after subscription tiers roll out.
  const isPremium = article?.audioTier === "premium";
  const paywalled = open && !user; // any unauthenticated listener sees the wall
  const effectiveSrc = article?.externalAudioUrl || article?.audio;

  // When paywall is shown, trigger the global signup modal AND close the audio modal
  // so we don't stack overlays.
  useEffect(() => {
    if (paywalled) {
      requireAuth(isPremium ? "premium" : "audio");
      onClose?.();
    }
  }, [paywalled, isPremium, requireAuth, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === " ") {
        e.preventDefault();
        togglePlay();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset when opened for a different article. Critical: we call load() to
  // force the browser to fetch the new src — otherwise it keeps playing the
  // previous article's buffered audio even though the src attribute changed.
  useEffect(() => {
    if (!open || !audioRef.current) return;
    const el = audioRef.current;
    el.pause();
    el.currentTime = 0;
    el.playbackRate = SPEEDS[speedIdx];
    setProgress(0);
    beaconFiredRef.current = false;
    // Force the audio element to reload the new source before playing.
    try { el.load(); } catch { /* ignore */ }
    el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, article?.audio, article?.externalAudioUrl]);

  const fireBeaconOnce = () => {
    if (beaconFiredRef.current || !article?.id) return;
    beaconFiredRef.current = true;
    AudioAPI.play(article.id).catch(() => {
      // Silent — beacon failures must never disturb playback
      beaconFiredRef.current = false;
    });
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const seek = (delta) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(duration || 0, a.currentTime + delta));
  };

  const seekTo = (frac) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    a.currentTime = duration * frac;
  };

  const cycleSpeed = () => {
    const nxt = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(nxt);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[nxt];
  };

  if (!open || !article) return null;

  const dateStr = article.publishedAt
    ? new Date(article.publishedAt)
        .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
        .toUpperCase() +
      ", AT " +
      new Date(article.publishedAt).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0B1F33] text-[#D7DEE5] flex flex-col"
      data-testid="audio-player-modal"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-[#1f3454]">
        <button
          onClick={onClose}
          data-testid="audio-return-to-site"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#D7DEE5]/80 hover:text-[#0891B2] transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Return to Site
        </button>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold">
          <Headphones className="w-3.5 h-3.5" /> Vexent Wire Audio
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#132f57] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto flex items-center justify-center px-5 md:px-8 py-8">
        {paywalled ? (
          <div className="w-full max-w-[520px] text-center" data-testid="audio-paywall">
            <Lock className="w-8 h-8 text-[#0891B2] mx-auto mb-6" />
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold mb-3">
              Premium Audio
            </div>
            <h2 className="font-serif-display text-[22px] md:text-[28px] font-bold text-[#FFFFFF] leading-tight">
              {article.title}
            </h2>
            <p className="mt-4 font-serif-display italic text-[14px] text-[#D7DEE5]/80 leading-relaxed">
              Full-length audio narration is a Vexent Wire Premium benefit.
              Subscribe to unlock the entire audio library.
            </p>
            <button
              className="mt-8 inline-flex items-center gap-2 bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] px-6 py-3 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors"
              data-testid="audio-paywall-cta"
              onClick={onClose}
            >
              Become a Subscriber
            </button>
          </div>
        ) : (
        <div className="w-full max-w-[720px] text-center">
          {article.image && (
            <div className="w-40 h-40 md:w-56 md:h-56 mx-auto overflow-hidden mb-8 border border-[#B8E9F1]/40">
              <img src={article.image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold mb-3">
            Now Playing
          </div>
          <h2 className="font-serif-display text-[24px] md:text-[34px] font-bold text-[#FFFFFF] leading-tight">
            {article.title}
          </h2>
          {article.dek && (
            <p className="mt-3 font-serif-display italic text-[14px] md:text-[16px] text-[#D7DEE5]/80 leading-relaxed">
              {article.dek}
            </p>
          )}
          <div className="mt-4 text-[11px] tracking-wider uppercase text-[#D7DEE5]/60">
            <span>By {article.author || "Max Fischer"}</span>
            {dateStr && (
              <>
                <span className="mx-2 text-[#D7DEE5]/30">·</span>
                <span className="font-mono-tick tracking-normal normal-case">{dateStr}</span>
              </>
            )}
          </div>

          {/* Scrub bar */}
          <div className="mt-10">
            <div
              className="relative h-1.5 bg-[#132f57] cursor-pointer group"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                seekTo((e.clientX - r.left) / r.width);
              }}
            >
              <div
                className="absolute inset-y-0 left-0 bg-[#B8E9F1] transition-[width] duration-100"
                style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#B8E9F1] opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${duration ? (progress / duration) * 100 : 0}% - 6px)` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[11px] font-mono-tick text-[#D7DEE5]/60">
              <span data-testid="audio-current-time">{fmt(progress)}</span>
              <span data-testid="audio-duration">{fmt(duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-8 flex items-center justify-center gap-6">
            <button
              onClick={() => seek(-15)}
              className="w-11 h-11 rounded-full hover:bg-[#132f57] flex items-center justify-center transition-colors"
              aria-label="Skip back 15 seconds"
            >
              <Rewind className="w-5 h-5" />
            </button>
            <button
              onClick={togglePlay}
              data-testid="audio-play-toggle"
              className="w-16 h-16 rounded-full bg-[#B8E9F1] hover:bg-[#9FDDE7] flex items-center justify-center transition-colors shadow-lg"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <Pause className="w-6 h-6 text-[#0B1F33]" />
              ) : (
                <Play className="w-6 h-6 text-[#0B1F33] ml-0.5" />
              )}
            </button>
            <button
              onClick={() => seek(15)}
              className="w-11 h-11 rounded-full hover:bg-[#132f57] flex items-center justify-center transition-colors"
              aria-label="Skip forward 15 seconds"
            >
              <FastForward className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center">
            <button
              onClick={cycleSpeed}
              className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] hover:text-[#FFFFFF] transition-colors border border-[#B8E9F1]/40 px-3 py-1"
            >
              Speed · {SPEEDS[speedIdx]}×
            </button>
          </div>
        </div>
        )}
      </div>

      <audio
        key={effectiveSrc}
        ref={audioRef}
        src={effectiveSrc}
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onPlay={() => { setPlaying(true); fireBeaconOnce(); }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        preload="metadata"
      />
    </div>
  );
};

export default AudioPlayerModal;
